import '../documents.css';

import React, { useMemo, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';

import {
  getReportScoreDescription,
  getReportScoreName,
  getScoreTitleFontSize,
  reportVersionToReportConfig,
} from './reportVersions';

import { getTenantColor } from '../../../static/tenantConfigs';
import { StringToBooleanMap, StringToNumberMap } from '../../../types/common';
import { StarRating } from '../../controls/input/StarRating';
import { useOpenGlobalModal } from '../../../recoil/hooks/useOpenGlobalModal';
import { translate } from '../../../../common/language/translations';
import { useElementWidth } from '../../../../common/hooks/WindowSize';


interface ReportScoresProps {
  reportVersion: string;
  scoreValues: StringToNumberMap;
  averageMatchReportScores?: StringToNumberMap;
  handleRatingChange?: (scoreKey: string, value: number) => void;
  requiredScoresToShow?: StringToBooleanMap;
}


export const ReportScores: React.FC<ReportScoresProps> = ({
  reportVersion,
  scoreValues,
  averageMatchReportScores,
  handleRatingChange,
  requiredScoresToShow,
}) => {

  const userConfig = useRecoilValue(userConfigState);
  const { openTextModal } = useOpenGlobalModal();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const measuredWidth = useElementWidth(containerRef);


  const scoreKeys = useMemo(
    () => reportVersionToReportConfig[reportVersion]?.scoreKeys ?? [[]],
    [reportVersion]
  );

  const maxColumns = scoreKeys.length === 1
    ? scoreKeys[0].length <= 4
      ? 'one-column'
      : scoreKeys[0].length <= 10
        ? 'two-columns'
        : 'flex-grid'
    : 'flex-grid';

  const tenantColor = getTenantColor(userConfig?.club ?? '');

  const scorePerRow = useMemo(() => {
    if (!measuredWidth || Number.isNaN(measuredWidth)) return 1;
    return Math.max(1, Math.floor(measuredWidth / 150));
  }, [measuredWidth]);

  const numberOfTotalRows = useMemo(
    () => scoreKeys.reduce((acc, arr) => acc + Math.ceil(arr.length / scorePerRow), 0),
    [scoreKeys, scorePerRow]
  );

  return (
    <div className={'report-scores-container'} ref={containerRef}>

      {scoreKeys.map((scoreKeyArray, scoreKeyArrayIndex) => {

        const numberOfRows = Math.ceil(scoreKeyArray.length / scorePerRow);
        const scoreProportion = scoreKeys.length === 1 ? 1 : numberOfRows / numberOfTotalRows;

        return (
          <React.Fragment key={scoreKeyArrayIndex}>

            {scoreKeyArrayIndex > 0 && <div className='report-scores-array-container-divider' />}

            <div
              className={'report-scores-array-container report-scores-array-container-' + maxColumns}
              style={{
                height: `${scoreProportion * 100}%`,
              }}
            >
              {scoreKeyArray.map((scoreKey, scoreKeyIndex) => {

                const isLargeRating = maxColumns !== 'flex-grid' && (maxColumns === 'one-column' || scoreKeyIndex <= 1);

                const fontSize = isLargeRating
                  ? 22
                  : 19;

                const paddingBottom = isLargeRating && maxColumns !== 'one-column'
                  ? '1vh'
                  : undefined;

                const showRequired = requiredScoresToShow && requiredScoresToShow[scoreKey + 'ShowRequired'];

                const scoreName = getReportScoreName(scoreKey, userConfig?.language, userConfig?.club) ?? scoreKey;
                const scoreDescription = getReportScoreDescription(scoreKey, userConfig?.language, userConfig?.club);

                return (
                  <div
                    key={scoreKey}
                    className='report-rating'
                    style={{
                      paddingBottom: paddingBottom,
                    }}
                  >
                    <div
                      className={'report-rating-title' + (scoreDescription ? ' report-rating-title-clickable' : '')}
                      style={{ fontSize: getScoreTitleFontSize(scoreName, 14, 12) }}
                      onClick={() => scoreDescription ? openTextModal(scoreName, scoreDescription) : undefined}
                      title={scoreDescription ? translate('showDescription', userConfig?.language) : undefined}
                    >
                      {scoreName}
                    </div>

                    <div className='report-rating-stars'>
                      <StarRating
                        value={scoreValues[scoreKey]}
                        defaultValue={averageMatchReportScores?.[scoreKey]}
                        onChange={handleRatingChange ? (value: number | null) => handleRatingChange(scoreKey, value ?? 0) : undefined}
                        size={fontSize}
                        color={tenantColor}
                        borderColor={handleRatingChange ? '#a7a9ad' : '#9d9fa3'}
                      />

                      {showRequired && (
                        <div
                          className={
                            'document-required-star report-field-required'
                            + (isLargeRating ? ' report-large-rating-required' : ' report-rating-required')
                            + ' fast-fade-in'
                          }
                        >
                          *
                        </div>
                      )}
                    </div>

                  </div>
                );
              })}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};
